/**
 * Central lead-tracking dispatcher for PUBLIC landing pages.
 *
 * One entry point (`fireLeadTracking`) that every lead interaction calls — form
 * success, tel: click, mailto: click — so conversion logic never scatters into
 * the section components. It decides which configured platform adapters fire for
 * that specific lead source and builds each platform's event from OUR code
 * (there is no pasted script anywhere in this feature).
 *
 * Non-negotiables (from the brief):
 *   - CONSENT FIRST: fire nothing unless the visitor accepted (same gate as
 *     TrackingScripts — localStorage 'linkhub-consent' === 'granted').
 *   - DEFENSIVE: a missing/blocked pixel, an ad-blocker, a throw — none of it
 *     may break the underlying form/tel/mailto action. Every call is guarded.
 *   - A platform fires only if its PAGE-LEVEL tag exists AND this lead source
 *     has that platform configured.
 */


import { hasConsent } from '@/lib/consent';

// Lead-level platform key -> the page-level tracking_tags type it depends on.
const REQUIRES_TAG = {
  google_ads: 'google_ads',
  meta: 'meta_pixel',
  linkedin: 'linkedin_insight',
  microsoft: 'microsoft_uet',
  tiktok: 'tiktok_pixel',
  x: 'x_pixel',
};

/** First page-level tag id of a given type, or null. */
function pageTagId(page, type) {
  const tag = (page?.tracking_tags || []).find((t) => t && t.type === type && t.id);
  return tag ? tag.id : null;
}

/** Tracking is best-effort: never let an adapter throw into the lead action. */
function safe(fn) {
  try {
    fn();
  } catch {
    /* swallow — a tracking failure must not affect the visitor's action */
  }
}

/**
 * Notify every configured, consented PAID-MEDIA platform that a lead occurred.
 * GA4 is not handled here — see the note below.
 *
 * @param {object}  args
 * @param {object}  args.page            resolved public page (has tracking_tags + lead_tracking)
 * @param {string}  args.sourceId        stable lead-source id (see leadSources.js)
 * @param {string} [args.transactionId]  optional dedup id (e.g. the form
 *                                       submission id) -> Google Ads transaction_id
 */
export function fireLeadTracking({ page, sourceId, transactionId }) {
  if (!page || !sourceId) return;
  if (!hasConsent()) return;

  const config = page.lead_tracking && page.lead_tracking[sourceId];
  if (!config) return;

  const w = window;
  const has = (platformKey) => !!pageTagId(page, REQUIRES_TAG[platformKey]);

  // NB: GA4 is deliberately absent here. `generate_lead` is a platform-level
  // contract pushed to the global GTM dataLayer for EVERY recognised lead
  // (see useGTM.trackLead), independent of this optional paid-media config.

  // Google Ads — page-level AW tag id + this source's conversion label.
  if (config.google_ads?.conversion_label && has('google_ads') && typeof w.gtag === 'function') {
    const id = pageTagId(page, 'google_ads');
    const payload = { send_to: `${id}/${config.google_ads.conversion_label}` };
    if (transactionId) payload.transaction_id = transactionId;   // dedup retried submits
    safe(() => w.gtag('event', 'conversion', payload));
  }

  // Meta — a standard event (fires to every inited pixel).
  if (config.meta?.event && has('meta') && typeof w.fbq === 'function') {
    safe(() => w.fbq('track', config.meta.event));
  }

  // LinkedIn — conversion by numeric id.
  if (config.linkedin?.conversion_id && has('linkedin') && typeof w.lintrk === 'function') {
    safe(() => w.lintrk('track', { conversion_id: Number(config.linkedin.conversion_id) }));
  }

  // Microsoft Advertising — custom UET event. uetq is an array pre-init and a
  // UET instance after; both expose .push.
  if (config.microsoft?.event_action && has('microsoft') &&
      w.uetq && typeof w.uetq.push === 'function') {
    const meta = config.microsoft.event_category
      ? { event_category: config.microsoft.event_category }
      : {};
    safe(() => w.uetq.push('event', config.microsoft.event_action, meta));
  }

  // TikTok — a standard event.
  if (config.tiktok?.event && has('tiktok') && w.ttq && typeof w.ttq.track === 'function') {
    safe(() => w.ttq.track(config.tiktok.event));
  }

  // X / Twitter — an event tag id.
  if (config.x?.event_id && has('x') && typeof w.twq === 'function') {
    safe(() => w.twq('event', config.x.event_id, {}));
  }
}
